/* Luxe & Gloss cart rendering and drawer accessibility, version 1. */
(function () {
  'use strict'

  window.LG = window.LG || {}
  if (window.LG.cartUIVersion === 1) return
  window.LG.cartUIVersion = 1

  const renderedRequests = new Map()
  let refreshQueue = Promise.resolve()

  function parseMarkup (markup) {
    return new DOMParser().parseFromString(markup, 'text/html')
  }

  function renderedSectionIds (sections) {
    return new Set(Object.entries(sections || {})
      .filter(([, markup]) => typeof markup === 'string')
      .map(([sectionId]) => sectionId))
  }

  function focusDescriptor () {
    const active = document.activeElement
    if (!active) return null

    const root = active.closest && active.closest('[data-lg-cart-root]')
    if (active.closest && active.closest('[data-lg-cart-note-form]')) {
      return { role: 'status', context: root && root.dataset.lgCartContext }
    }

    const key = active.dataset && active.dataset.lgCartKey
    const role = active.dataset && (
      active.dataset.lgQuantityAction ||
      (active.matches('[data-lg-cart-quantity]') ? 'quantity' : null) ||
      (active.matches('[data-lg-cart-remove]') ? 'remove' : null)
    )
    if (key && role) return { key, role, context: root && root.dataset.lgCartContext }

    return null
  }

  function restoreFocus (descriptor, scope) {
    if (!descriptor || !scope) return

    if (descriptor.role === 'status') {
      scope.querySelector('[data-lg-cart-status]')?.focus()
      return
    }

    const candidates = scope.querySelectorAll('[data-lg-cart-key]')
    const target = [...candidates].find(candidate => {
      if (candidate.dataset.lgCartKey !== descriptor.key) return false
      const role = candidate.dataset.lgQuantityAction ||
        (candidate.matches('[data-lg-cart-quantity]') ? 'quantity' : null) ||
        (candidate.matches('[data-lg-cart-remove]') ? 'remove' : null)
      return role === descriptor.role
    })

    if (target) target.focus()
    else scope.querySelector('[data-lg-cart-status]')?.focus()
  }

  function replaceAll (selector, nextElement) {
    if (!nextElement) return
    document.querySelectorAll(selector).forEach(current => current.replaceWith(nextElement.cloneNode(true)))
  }

  function replaceSections (sections) {
    const descriptor = focusDescriptor()
    let focusScope = null

    Object.entries(sections || {}).forEach(([sectionId, markup]) => {
      if (typeof markup !== 'string') return
      const nextDocument = parseMarkup(markup)

      if (sectionId === 'cart-count-badge') {
        replaceAll('.cart-count-badge', nextDocument.querySelector('.cart-count-badge'))
      }

      if (sectionId === 'offcanvas-cart') {
        const current = document.querySelector('[data-lg-cart-drawer] [data-lg-cart-root]')
        const next = nextDocument.querySelector('[data-lg-cart-root]')
        if (current && next) {
          current.replaceWith(next)
          if (!descriptor || descriptor.context === 'drawer') {
            focusScope = document.querySelector('[data-lg-cart-drawer]')
          }
        }
      }

      if (sectionId === 'lg-cart-main') {
        const current = document.querySelector('[data-lg-cart-page]')
        const next = nextDocument.querySelector('[data-lg-cart-page]')
        if (current && next) {
          current.replaceWith(next)
          if (!descriptor || descriptor.context === 'page') {
            focusScope = document.querySelector('[data-lg-cart-page]')
          }
        }
      }
    })

    restoreFocus(descriptor, focusScope)
  }

  function cartSectionUrl () {
    const routes = window.Shopify && window.Shopify.routes ? window.Shopify.routes : {}
    const cartUrl = routes.cart_url || `${routes.root || '/'}cart`
    const url = new URL(cartUrl, window.location.origin)
    url.searchParams.set('sections', 'cart-count-badge,offcanvas-cart,lg-cart-main')
    return `${url.pathname}${url.search}`
  }

  function refreshSections (requestId) {
    refreshQueue = refreshQueue.then(async () => {
      let lastError

      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const response = await window.fetch(cartSectionUrl(), {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' }
          })

          if (!response.ok) throw new Error(`Section refresh failed with ${response.status}.`)
          const sections = await response.json()
          replaceSections(sections)
          renderedRequests.set(requestId, renderedSectionIds(sections))
          if (!hasRequiredSections(requestId)) throw new Error('Section refresh was incomplete.')
          window.setTimeout(() => renderedRequests.delete(requestId), 1000)
          return
        } catch (error) {
          lastError = error
        }
      }

      throw lastError
    }).catch(() => {
      document.querySelectorAll('[data-lg-cart-error]').forEach(alert => {
        const message = alert.querySelector('[data-lg-cart-error-message]')
        if (message) message.textContent = 'Your bag was updated, but this view could not refresh. Reload this page to see the latest state.'
        alert.removeAttribute('hidden')
      })
    })
  }

  function hasRequiredSections (requestId) {
    const rendered = renderedRequests.get(requestId)
    if (!rendered) return false
    if (document.querySelector('[data-lg-cart-drawer]') && !rendered.has('offcanvas-cart')) return false
    if (document.querySelector('[data-lg-cart-page]') && !rendered.has('lg-cart-main')) return false
    return rendered.has('cart-count-badge')
  }

  function updateCount (cart) {
    if (!cart || !Number.isFinite(cart.item_count)) return

    document.querySelectorAll('.cart-count-badge').forEach(badge => {
      badge.dataset.count = String(cart.item_count)
      badge.textContent = String(cart.item_count)

      const control = badge.closest('[aria-controls="offcanvas-cart"]')
      if (control) {
        const currentLabel = control.getAttribute('aria-label') || 'Cart'
        const baseLabel = currentLabel.replace(/\s+\d+\s*$/, '')
        control.setAttribute('aria-label', `${baseLabel} ${cart.item_count}`)
      }
    })
  }

  function initializeDrawer (drawer) {
    if (!drawer || drawer.dataset.lgDrawerReady === 'true') return
    drawer.dataset.lgDrawerReady = 'true'

    const closeButton = drawer.querySelector('[data-lg-cart-close]')
    let directTrigger = null

    drawer.addEventListener('show.bs.offcanvas', event => {
      directTrigger = event.relatedTarget || document.activeElement
      drawer.setAttribute('aria-modal', 'true')
      drawer.removeAttribute('aria-hidden')
    })

    drawer.addEventListener('shown.bs.offcanvas', () => {
      closeButton?.focus()
    })

    drawer.addEventListener('hidden.bs.offcanvas', () => {
      drawer.removeAttribute('aria-modal')
      drawer.setAttribute('aria-hidden', 'true')
      const trigger = directTrigger || (window.LG && window.LG.cart && window.LG.cart.getLastTrigger())
      if (trigger && trigger.isConnected) trigger.focus()
    })

    closeButton?.addEventListener('click', () => {
      if (window.bootstrap && window.bootstrap.Offcanvas) return
      drawer.classList.remove('show')
      drawer.setAttribute('hidden', '')
      drawer.removeAttribute('aria-modal')
      drawer.setAttribute('aria-hidden', 'true')
      document.body.classList.remove('lg-cart-drawer-open')
      const trigger = directTrigger || (window.LG && window.LG.cart && window.LG.cart.getLastTrigger())
      if (trigger && trigger.isConnected) trigger.focus()
    })

    drawer.addEventListener('keydown', event => {
      if (window.bootstrap && window.bootstrap.Offcanvas) return

      if (event.key === 'Escape') {
        event.preventDefault()
        closeButton?.click()
        return
      }

      if (event.key !== 'Tab') return
      const focusable = [...drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter(element => !element.hasAttribute('hidden'))
      if (!focusable.length) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    })
  }

  document.querySelectorAll('[data-lg-cart-drawer]').forEach(initializeDrawer)

  document.addEventListener('shopify:section:load', event => {
    event.target.querySelectorAll('[data-lg-cart-drawer]').forEach(initializeDrawer)
  })

  window.addEventListener('lg:cart:sections', event => {
    const detail = event.detail || {}
    replaceSections(detail.sections)
    if (detail.requestId) renderedRequests.set(detail.requestId, renderedSectionIds(detail.sections))
  })

  window.addEventListener('lg:cart:updated', event => {
    const detail = event.detail || {}
    updateCount(detail.cart)

    if (detail.requestId && !hasRequiredSections(detail.requestId)) {
      refreshSections(detail.requestId)
    }

    window.setTimeout(() => renderedRequests.delete(detail.requestId), 1000)
  })
}())
